import config from 'js/app/config';
import logger from 'js/app/loggerSingleton';
import user from 'js/lib/user';
import { generateUUID } from 'js/lib/util';

import inServerContext from 'bundles/ssr/util/inServerContext';

import type { RedirectInfo } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PromiseRejectionReason = any; // we can't do better than 'any' here: https://stackoverflow.com/a/50071254/16366156

const EVENTING_URL = config.url.base + 'api/rest/v1/eventing/infobatch'; // must be absolute URL, otherwise node.js SSR throws an error
const EVENTING_APP_NAME = 'help_redirects';
const EVENT_KEY = 'help.redirection.redirect'; // must be at least 3 parts: app.page.action

// Note that PAYLOAD_CONTENT is included twice in eventing payloads: Once at the top level, and again inside each batched event.
const PAYLOAD_CONTENT = {
  clientType: 'web',
  app: 'phoenix',
  clientVersion: 'helpredirects',
};

const sendToServer = (event: $TSFixMe) => {
  // Derived from bundles/notification-service-worker/fireEvents.ts
  const method = 'POST';
  const credentials: RequestCredentials = 'include'; // indicates that cookies should be sent with the request
  const headers = new Headers({
    'cache-control': 'no-cache',
    'content-type': 'application/json; charset=UTF-8',
    // unlike normal APIs, we don't need a CSRF token to send these events
  });
  const body = JSON.stringify({
    ...PAYLOAD_CONTENT,
    events: [event],
  });

  return fetch(EVENTING_URL, { method, credentials, headers, body });
};

/**
 * Sends a Next Gen (NG) Eventing framework event to the server (aka Retracked / prod.raw_events)
 * We can't use the normal frontend eventing utils for this because they don't work under SSR, and we don't have JS running in the browser because we issue a 301/302 redirect from SSR
 * The challenge of firing events outside of the normal browser environment was previously encountered by a team that needed to log an event from a service worker, and this code was derived from that: bundles/notification-service-worker/fireEvents.ts
 */
export const fireEvent = (
  appName: string,
  url: string,
  eventKey: string,
  eventValue: Record<string, string | number | boolean>
) => {
  const eventData = {
    ...PAYLOAD_CONTENT,
    guid: generateUUID(),
    key: eventKey,
    url,
    visitId: generateUUID(), // The user's visit ID is only available client side, but this redirection should only happen on the server (in SSR).  We generate a new one for these events.
    clientTimestamp: Date.now(),
    value: {
      appName,
      ...eventValue,
    },
  };

  return sendToServer(eventData).catch((reason: PromiseRejectionReason) => {
    // Since we might not want to await a tracking call, an unhandled promise rejection would crash the whole SSR node.js process, which is bad.
    // We'll log the error and continue gracefully, since this is just for tracking and not a fatal error scenario.
    logger.error(
      'Error: Unable to send help redirection tracking event',
      JSON.stringify(reason, Object.getOwnPropertyNames(reason))
    );
  });
};

export const trackHelpRedirect = (redirectInfo: RedirectInfo) => {
  try {
    const userId = user.get()?.id;
    const isSsr = inServerContext;

    const eventValue = {
      ...redirectInfo,
      isSsr,
      userId, // Including userId here because I'm not sure whether the backend automatically includes this based on CAUTH, and I'm not even sure if CAUTH is getting included in the request from SSR
    };
    const url = config.url.base + 'hc'; // During help center redirects the current URL is always coursera.org/hc.  We can't acccess location.href in SSR, and in any case this information is not useful for analytics -- the redirectInfo object has all the data.

    logger.info('Tracking help redirect', eventValue); // Please leave this in.  It enables us to troubleshoot via STDOUT in local dev and SumoLogic SSR logs in prod.

    if (!inServerContext) {
      return; // we're in the browser due to an issue.  Assume the event was already tracked from the server to avoid double logging.
    }

    fireEvent(EVENTING_APP_NAME, url, EVENT_KEY, eventValue); // returns a promise, but we're intentionally not awaiting it so that logging the event is a background task instead of holding up the redirection
  } catch (error) {
    // It seems something goes wrong with this code in lambda SSR preview
    logger.error('Error: Exception while tracking help redirect', error);
  }
};
